import React, { useState, useEffect } from 'react';
import { usePlatformConfig } from '../hooks/usePlatformConfig';
import Layout from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const ROLE_ICONS = {
  controller: '🎛️',
  compute: '⚡',
  memory: '🧠',
  storage: '💾',
  gpu: '🎮',
  worker: '⚙️',
};

const ROLE_COLORS = {
  controller: 'bg-blue-900 text-blue-300',
  compute: 'bg-yellow-900 text-yellow-300',
  memory: 'bg-purple-900 text-purple-300',
  storage: 'bg-green-900 text-green-300',
  gpu: 'bg-red-900 text-red-300',
  worker: 'bg-slate-700 text-slate-300',
};

const STATUS_COLORS = {
  provisioning: 'text-yellow-400',
  running: 'text-green-400',
  degraded: 'text-orange-400',
  stopped: 'text-gray-400',
  failed: 'text-red-400',
  deleted: 'text-gray-500',
};

function ClusterCard({ cluster, onSelect, onDelete }) {
  const nodeRunning = cluster.nodes?.filter(n => n.status === 'running').length || 0;
  const nodeTotal = cluster.node_count || 0;

  return (
    <div className="bg-slate-800 rounded-lg p-5 border border-slate-700 hover:border-slate-500 transition-colors cursor-pointer"
         onClick={() => onSelect(cluster)}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-white font-semibold text-lg">{cluster.name}</h3>
          <p className="text-gray-400 text-sm capitalize">{cluster.cluster_type?.replace(/-/g, ' ')} · {cluster.region}</p>
        </div>
        <span className={`text-sm font-medium capitalize ${STATUS_COLORS[cluster.status] || 'text-gray-400'}`}>
          {cluster.status === 'running' && '● '}{cluster.status}
        </span>
      </div>

      <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
        <span>🖥️ {nodeTotal} nodes</span>
        {cluster.status === 'provisioning' && (
          <span className="text-yellow-400">{nodeRunning}/{nodeTotal} ready</span>
        )}
        {cluster.ssh_command && (
          <span className="text-green-400 font-mono text-xs truncate">{cluster.ssh_command}</span>
        )}
      </div>

      <div className="flex justify-between items-center">
        <span className="text-gray-500 text-xs">
          Created {new Date(cluster.created_at).toLocaleDateString()}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(cluster); }}
          className="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded hover:bg-red-900/30"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function TemplateCard({ template, onSelect }) {
  return (
    <div
      className="bg-slate-800 rounded-lg p-5 border border-slate-700 hover:border-blue-500 transition-colors cursor-pointer"
      onClick={() => onSelect(template)}
    >
      <div className="text-3xl mb-3">{template.icon}</div>
      <h3 className="text-white font-semibold mb-1">{template.name}</h3>
      <p className="text-gray-400 text-sm mb-3">{template.description}</p>
      <div className="flex flex-wrap gap-1">
        {template.node_specs?.map((spec, i) => (
          <span key={i} className="text-xs bg-slate-700 text-gray-300 px-2 py-0.5 rounded">
            {spec.count}x {spec.label || spec.role}
          </span>
        ))}
      </div>
    </div>
  );
}

function CreateClusterModal({ template, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [region, setRegion] = useState('us-east');
  const [nodeSpecs, setNodeSpecs] = useState(template?.node_specs || []);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const updateNodeCount = (index, count) => {
    const updated = [...nodeSpecs];
    updated[index] = { ...updated[index], count: Math.max(1, parseInt(count) || 1) };
    setNodeSpecs(updated);
  };

  const handleCreate = async () => {
    if (!name.trim()) { setError('Cluster name is required'); return; }
    setCreating(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/v1/clusters`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          template_slug: template?.slug,
          region,
          node_specs: nodeSpecs,
        })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to create cluster'); return; }
      onCreate(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div className="flex items-center space-x-3">
            <span className="text-3xl">{template?.icon || '🖥️'}</span>
            <div>
              <h2 className="text-white font-bold text-lg">{template?.name || 'Custom Cluster'}</h2>
              <p className="text-gray-400 text-sm">Configure your cluster</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Cluster Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="my-ml-cluster"
              autoCapitalize="none"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-blue-500 outline-none"
            />
          </div>

          {/* Region */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Region</label>
            <select
              value={region}
              onChange={e => setRegion(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="us-east">US East</option>
              <option value="us-west">US West</option>
              <option value="eu-west">EU West</option>
              <option value="eu-central">EU Central</option>
              <option value="ap-southeast">AP Southeast</option>
            </select>
          </div>

          {/* Node specs */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Node Configuration</label>
            <div className="space-y-2">
              {nodeSpecs.map((spec, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-700 rounded-lg px-3 py-2">
                  <div className="flex items-center space-x-2">
                    <span>{ROLE_ICONS[spec.role] || '⚙️'}</span>
                    <div>
                      <p className="text-white text-sm font-medium">{spec.label || spec.role}</p>
                      <p className="text-gray-400 text-xs">{spec.instance_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => updateNodeCount(i, spec.count - 1)}
                      className="w-6 h-6 bg-slate-600 hover:bg-slate-500 rounded text-white text-sm"
                    >−</button>
                    <span className="text-white w-6 text-center">{spec.count}</span>
                    <button
                      onClick={() => updateNodeCount(i, spec.count + 1)}
                      className="w-6 h-6 bg-slate-600 hover:bg-slate-500 rounded text-white text-sm"
                    >+</button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-xs mt-2">
              Total: {nodeSpecs.reduce((sum, s) => sum + (s.count || 1), 0)} nodes
            </p>
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}
        </div>

        <div className="flex space-x-3 p-5 border-t border-slate-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium"
          >
            {creating ? 'Provisioning...' : 'Deploy Cluster'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClusterDetailModal({ cluster, onClose, onDelete, config }) {
  const [detail, setDetail] = useState(cluster);

  useEffect(() => {
    const fetchDetail = async () => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/v1/clusters/${cluster.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setDetail(await res.json());
    };
    fetchDetail();
    const interval = setInterval(fetchDetail, 10000);
    return () => clearInterval(interval);
  }, [cluster.id]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-700 sticky top-0 bg-slate-800">
          <div>
            <h2 className="text-white font-bold text-lg">{detail.name}</h2>
            <p className={`text-sm capitalize ${STATUS_COLORS[detail.status] || 'text-gray-400'}`}>
              {detail.status} · {detail.cluster_type?.replace(/-/g, ' ')} · {detail.region}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Nodes */}
          <div>
            <h3 className="text-white font-semibold mb-3">Cluster Nodes</h3>
            <div className="space-y-2">
              {detail.nodes?.map((node, i) => (
                <div key={node.id} className="flex items-center justify-between bg-slate-700 rounded-lg px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{ROLE_ICONS[node.role] || '⚙️'}</span>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${ROLE_COLORS[node.role] || 'bg-slate-600 text-gray-300'}`}>
                          {node.role}
                        </span>
                        {node.ssh_port > 0 && (
                          <span className="text-green-400 text-xs font-mono">
                            port {node.ssh_port}
                          </span>
                        )}
                      </div>
                      {node.private_ip && (
                        <p className="text-gray-400 text-xs mt-0.5 font-mono">{node.private_ip}</p>
                      )}
                    </div>
                  </div>
                  <span className={`text-sm ${STATUS_COLORS[node.status] || 'text-gray-400'}`}>
                    {node.status}
                  </span>
                </div>
              ))}
              {(!detail.nodes || detail.nodes.length === 0) && (
                <p className="text-gray-500 text-sm">Nodes are provisioning...</p>
              )}
            </div>
          </div>

          {/* SSH Access */}
          {detail.nodes?.find(n => n.role === 'controller' && n.ssh_port > 0) && (
            <div className="bg-slate-900 rounded-lg p-4">
              <p className="text-gray-400 text-xs mb-2">SSH to Controller Node</p>
              <code className="text-green-400 text-sm font-mono">
                ssh -p {detail.nodes.find(n => n.role === 'controller').ssh_port} ubuntu@ssh.{config.platform_domain || 'your-platform'}
              </code>
            </div>
          )}

          {/* Delete */}
          <div className="pt-2">
            <button
              onClick={() => { onDelete(detail); onClose(); }}
              className="w-full px-4 py-2 bg-red-900/50 hover:bg-red-900 text-red-300 rounded-lg text-sm"
            >
              Delete Cluster
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClusterDashboard() {
  const { config } = usePlatformConfig();
  const [clusters, setClusters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [view, setView] = useState('clusters'); // clusters | templates
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('access_token');

  const fetchClusters = async () => {
    try {
      const res = await fetch(`${API_URL}/v1/clusters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setClusters(await res.json());
    } catch {}
    setLoading(false);
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/v1/cluster-templates`);
      if (res.ok) setTemplates(await res.json());
    } catch {}
  };

  useEffect(() => {
    fetchClusters();
    fetchTemplates();
    const interval = setInterval(fetchClusters, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleDelete = async (cluster) => {
    if (!window.confirm(`Delete cluster "${cluster.name}" and all its nodes?`)) return;
    const res = await fetch(`${API_URL}/v1/clusters/${cluster.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) fetchClusters();
  };

  const handleCreated = (data) => {
    setSelectedTemplate(null);
    fetchClusters();
  };

  return (
    <Layout userType="customer">
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Compute Clusters</h1>
          <p className="text-gray-400 text-sm mt-1">
            Distributed VM clusters for large-scale compute workloads
          </p>
        </div>
        <button
          onClick={() => setView(view === 'clusters' ? 'templates' : 'clusters')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
        >
          {view === 'clusters' ? '+ New Cluster' : '← My Clusters'}
        </button>
      </div>

      {view === 'templates' ? (
        <>
          <p className="text-gray-400 mb-4">Select a template to deploy a pre-configured cluster:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(t => (
              <TemplateCard key={t.id} template={t} onSelect={setSelectedTemplate} />
            ))}
          </div>
        </>
      ) : (
        <>
          {loading ? (
            <p className="text-gray-400">Loading clusters...</p>
          ) : clusters.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-4xl mb-4">🖥️</p>
              <p className="text-white font-semibold mb-2">No clusters yet</p>
              <p className="text-gray-400 mb-6">Deploy a distributed compute cluster for AI/ML, big data, HPC, or blockchain workloads</p>
              <button
                onClick={() => setView('templates')}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Browse Cluster Templates
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clusters.map(cl => (
                <ClusterCard
                  key={cl.id}
                  cluster={cl}
                  onSelect={setSelectedCluster}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      {selectedTemplate && (
        <CreateClusterModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onCreate={handleCreated}
        />
      )}

      {selectedCluster && (
        <ClusterDetailModal
          cluster={selectedCluster}
          config={config}
          onClose={() => setSelectedCluster(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
    </Layout>
  );
}
